import React from "react";
import { User, Phone, Mail, MapPin } from "lucide-react";
import Field from "./Field";

/**
 * Renders the 2-column name / phone / email / address grid used by
 * the main InquiryForm. Extracted from InquiryForm.jsx so the parent
 * can stay small.
 *
 * Each input keeps the SAME data-testid the original form shipped with
 * (form-name-input, form-phone-input, form-email-input, form-address-input)
 * so existing tests + analytics remain untouched.
 */
export default function InquiryContactFields({ values, errors, update }) {
    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field id="full_name" label="Full Name" error={errors.full_name}>
                <div className="relative">
                    <User
                        aria-hidden="true"
                        className="enc-input-icon h-4 w-4"
                    />
                    <input
                        id="full_name"
                        data-testid="form-name-input"
                        data-ph-mask="true"
                        className="enc-input with-icon ph-no-capture"
                        placeholder="Full Name"
                        value={values.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        autoComplete="name"
                        aria-required="true"
                        aria-invalid={!!errors.full_name}
                        aria-describedby={
                            errors.full_name ? "full_name-error" : undefined
                        }
                    />
                </div>
            </Field>

            <Field id="phone" label="Phone Number" error={errors.phone}>
                <div className="relative">
                    <Phone
                        aria-hidden="true"
                        className="enc-input-icon h-4 w-4"
                    />
                    <input
                        id="phone"
                        data-testid="form-phone-input"
                        data-ph-mask="true"
                        className="enc-input with-icon ph-no-capture"
                        placeholder="Phone Number"
                        value={values.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        inputMode="tel"
                        autoComplete="tel"
                        aria-required="true"
                        aria-invalid={!!errors.phone}
                        aria-describedby={
                            errors.phone ? "phone-error" : undefined
                        }
                    />
                </div>
            </Field>

            <Field id="email" label="Email Address" error={errors.email}>
                <div className="relative">
                    <Mail
                        aria-hidden="true"
                        className="enc-input-icon h-4 w-4"
                    />
                    <input
                        id="email"
                        data-testid="form-email-input"
                        data-ph-mask="true"
                        className="enc-input with-icon ph-no-capture"
                        placeholder="Email Address"
                        value={values.email}
                        onChange={(e) => update("email", e.target.value)}
                        type="email"
                        autoComplete="email"
                        aria-required="true"
                        aria-invalid={!!errors.email}
                        aria-describedby={
                            errors.email ? "email-error" : undefined
                        }
                    />
                </div>
            </Field>

            <Field id="address" label="Address" error={errors.address}>
                <div className="relative">
                    <MapPin
                        aria-hidden="true"
                        className="enc-input-icon h-4 w-4"
                    />
                    <input
                        id="address"
                        data-testid="form-address-input"
                        data-ph-mask="true"
                        className="enc-input with-icon ph-no-capture"
                        placeholder="Address"
                        value={values.address}
                        onChange={(e) => update("address", e.target.value)}
                        autoComplete="street-address"
                        aria-required="true"
                        aria-invalid={!!errors.address}
                        aria-describedby={
                            errors.address ? "address-error" : undefined
                        }
                    />
                </div>
            </Field>
        </div>
    );
}
